import { useRef, useState, useCallback, useMemo } from 'react';
import { breakpointKeys } from '../constants/breakpoints';
import combineBatches from '../helpers/combine-batches';
import getResolvedState from '../helpers/get-resolved-state';

const useSettingsState = (initialData = null, activeBreakpointKey = breakpointKeys.DEFAULT) => {
  const initialState = useRef(initialData?.initialState || {});
  const [state, setState] = useState(initialData?.state || {});
  const chunkTargets = useRef(initialData?.chunkTargets || new Map());
  const modifiedTargets = useRef(initialData?.modifiedTargets || new Map());

  const recordChanges = useCallback((batch, { chunkId = null } = {}) => {
    if (!chunkId) {
      return;
    }

    const recordedBreakpoints = modifiedTargets.current.get(chunkId) || new Map();

    Object.values(batch).forEach((targets) => {
      Object.entries(targets).forEach(([target, breakpointBasedValue]) => {
        Object.keys(breakpointBasedValue).forEach((breakpointKey) => {
          const changedTargets = recordedBreakpoints.get(breakpointKey) || new Set();

          changedTargets.add(target);
          recordedBreakpoints.set(breakpointKey, changedTargets);
        });
      });
    });

    modifiedTargets.current.set(chunkId, recordedBreakpoints);
  }, []);

  const getModifiedTargets = useCallback(
    (chunkId) => modifiedTargets.current.get(chunkId) || new Map(),
    []
  );

  const updateState = useCallback(
    (batch, { chunkId = null } = {}) => {
      if (!batch) {
        return;
      }

      recordChanges(batch, { chunkId });

      setState((currentState) => {
        const newState = combineBatches([currentState, batch]);

        if (chunkId) {
          chunkTargets.current.set(
            chunkId,
            new Set([
              ...(chunkTargets.current.get(chunkId) || new Set()),
              ...Object.values(batch).flatMap(Object.keys),
            ])
          );
        }

        return newState;
      });
    },
    [recordChanges]
  );

  const registerInitialState = useCallback((chunkId, chunkState) => {
    initialState.current = combineBatches([initialState.current, chunkState]);
    setState((currentState) => combineBatches([chunkState, currentState]));
  }, []);

  const getInitialState = useCallback(() => initialState.current, []);

  const clearStateByChunkId = useCallback((chunkId) => {
    const targetsToClear = chunkTargets.current.get(chunkId);

    if (!targetsToClear?.size) return;

    setState((currentState) => {
      const newState = { ...currentState };

      Object.keys(newState).forEach((elementId) => {
        targetsToClear.forEach((targetName) => delete newState[elementId][targetName]);

        if (!Object.keys(newState[elementId]).length) delete newState[elementId];
      });

      return newState;
    });

    chunkTargets.current.delete(chunkId);
    modifiedTargets.current.delete(chunkId);
  }, []);

  const resolvedState = useMemo(
    () => getResolvedState(state, activeBreakpointKey),
    [state, activeBreakpointKey]
  );

  const getPersistData = useCallback(
    () => ({
      initialState: initialState.current,
      state,
      chunkTargets: chunkTargets.current,
      modifiedTargets: modifiedTargets.current,
    }),
    [state]
  );

  return {
    state,
    resolvedState,
    updateState,
    getInitialState,
    getPersistData,
    getModifiedTargets,
    registerInitialState,
    clearStateByChunkId,
  };
};

export default useSettingsState;
